import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Product, Phase, PhaseDisplayNames } from '../../types/bom';
import { getProduct, updateProduct } from '../../services/bomService';
import { useProjects } from '../../hooks/useProject';

// Available phases
const PHASES: Phase[] = ['pre_bom', 'evt', 'dvt', 'pvt', 'mp'];

// Product categories
const PRODUCT_CATEGORIES = [
  { value: 'finished_good', label: 'Finished Good' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'pcba', label: 'PCBA' },
];

// Product statuses
const PRODUCT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'obsolete', label: 'Obsolete' },
];

const ProductEdit: React.FC = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the route prefix based on current path
  const getRoutePrefix = () => {
    const path = location.pathname;
    if (path.includes('/pcb-modules/')) {
      return '/app/bom/pcb-modules';
    } else if (path.includes('/assembled-modules/')) {
      return '/app/bom/assembled-modules';
    } else if (path.includes('/products/')) {
      return '/app/bom/products';
    }
    return '/app/bom/products'; // Default for legacy routes
  };
  const routePrefix = getRoutePrefix();
  const backUrl = routePrefix;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<Product | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    product_code: '',
    product_name: '',
    category: 'finished_good',
    project_code: '',
    target_mp_date: '',
    status: 'active',
    current_phase: 'pre_bom' as Phase,
  });

  // Fetch projects for dropdown
  const { data: projectsData } = useProjects();
  const projects = projectsData?.projects || [];

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId]);

  const loadProduct = async () => {
    if (!productId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getProduct(productId);
      setProduct(data);

      // Initialize form with current values
      setFormData({
        product_code: data.product_code || '',
        product_name: data.product_name || '',
        category: data.category || 'finished_good',
        project_code: data.project_code || '',
        target_mp_date: data.target_mp_date
          ? new Date(data.target_mp_date).toISOString().split('T')[0]
          : '',
        status: data.status || 'active',
        current_phase: data.current_phase || 'pre_bom',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productId || !product) return;

    if (!formData.product_code || !formData.product_name) {
      setError('Product code and name are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updateData: Partial<Product> = {
        product_code: formData.product_code,
        product_name: formData.product_name,
        category: formData.category,
        status: formData.status,
        current_phase: formData.current_phase,
      };

      if (formData.project_code) {
        updateData.project_code = formData.project_code;
      }

      if (formData.target_mp_date) {
        updateData.target_mp_date = new Date(formData.target_mp_date).toISOString();
      }

      await updateProduct(productId, updateData);
      navigate(`${routePrefix}/${productId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600 py-8">{error || 'Product not found'}</div>
        <button
          onClick={() => navigate(backUrl)}
          className="mx-auto block px-4 py-2 text-blue-600 hover:underline"
        >
          {t('common.back', 'Back to Products')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate(`${routePrefix}/${productId}`)}
        className="text-sm text-blue-600 hover:underline mb-4"
      >
        &larr; {t('common.back', 'Back to Product')}
      </button>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {t('bom.editProduct', 'Edit Product')}
      </h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 dark:bg-red-900/20 dark:border-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.productCode', 'Product Code')} *
          </label>
          <input
            type="text"
            value={formData.product_code}
            onChange={(e) => handleChange('product_code', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="e.g., PROD-001"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.productName', 'Product Name')} *
          </label>
          <input
            type="text"
            value={formData.product_name}
            onChange={(e) => handleChange('product_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="e.g., Smart Sensor Module"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.category', 'Category')}
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {PRODUCT_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.status', 'Status')}
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {PRODUCT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.phase', '階段 (Phase)')}
          </label>
          <select
            value={formData.current_phase}
            onChange={(e) => handleChange('current_phase', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {PHASES.map((phase) => (
              <option key={phase} value={phase}>
                {PhaseDisplayNames[phase]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.projectCode', 'Project Code')}
          </label>
          <select
            value={formData.project_code}
            onChange={(e) => handleChange('project_code', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">{t('bom.selectProject', 'Select a project (optional)')}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.project_code}>
                {project.project_code} - {project.project_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('bom.targetMPDate', 'Target MP Date')}
          </label>
          <input
            type="date"
            value={formData.target_mp_date}
            onChange={(e) => handleChange('target_mp_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        {/* Read-only info */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
            {t('bom.readOnlyInfo', 'Read-only Information')}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Current Revision:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100 font-medium">
                {product.current_revision}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Created:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">
                {new Date(product.created_at).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Updated:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">
                {new Date(product.updated_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
          </button>
          <button
            type="button"
            onClick={() => navigate(`${routePrefix}/${productId}`)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductEdit;
